import React, { useEffect, useState, useMemo } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { useAxiosInterceptors } from "utilities/api/axios.js";
import ReportIssue from "components/ReportIssue/ReportIssue.jsx";
import RoutesWrapper from "routes.js";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import posthog from "posthog-js";
import ChatBar from "components/Messaging/Chatbar.js";
import AdminMessagingAPIs from "./utilities/api/IMS/AdminMessagingAPIs.js";
import {
  setConversations,
  setMessagesByConversation,
} from "./redux/slices/conversationSlice.js";

export const App = () => {
  // Access current user from redux store
  const { user } = useSelector((state) => state.user);
  const [isInterceptorReady, setIsInterceptorReady] = useState(false);
  const dispatch = useDispatch();

  const isLoggedIn = useMemo(() => !!(user && user?.id), [user]);

  // Initialize PostHog analytics with environment key and config
  const PostHogAppId = process.env.REACT_APP_PUBLIC_POSTHOG_KEY;
  posthog.init(PostHogAppId, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "always",
  });

  // Identify the current user in PostHog
  posthog.identify(
    user.id,
    { userID: user.id, email: user.email, name: user.username },
    {
      is_staff: user.is_staff,
      is_student: user.is_student,
      is_active: user.is_active,
      TL_Name: user.TL_name,
    }
  );

  // Set up Axios interceptors using a custom hook
  useAxiosInterceptors(setIsInterceptorReady);

  // Fetch conversation threads and messages when user is available
  useEffect(() => {
    if (!user || !user.id) return; // Wait until user info is loaded

    const getThreads = async () => {
      try {
        // Fetch all threads from backend API
        const threads = await AdminMessagingAPIs.GetThreadList();

        // Filter threads involving current user (group or individual chats with more than 1 participant)
        const filteredThreads = threads.filter((thread) =>
          thread.is_group_chat
            ? thread.participants.some(
                (participant) => participant === user.username
              ) && thread.participants.length > 1
            : thread.participants.length > 1 &&
              thread.participants.some(
                (participant) => participant === user.username
              )
        );

        // Map filtered threads into conversation summaries for redux state
        const convs = filteredThreads.map((thread) => {
          let otherUser = null;
          let otherUserName = null;
          let otherUserId = null;

          if (thread.is_group_chat) {
            otherUserId = `${thread.id}_group`;
            otherUser = thread.group_name;
            otherUserName = thread.group_name;
          } else {
            otherUserId = thread.participant_ids.find(
              (participant) => participant !== user.id
            );
            otherUser = thread.participants.find(
              (participant) => participant !== user.username
            );
            otherUserName = thread.participant_names.find(
              (participant) => participant !== user.name
            );
          }

          const latestMessage = thread.latest_message;

          return {
            id: thread.id,
            userId: otherUserId || "Unknown",
            userName: otherUser || "Unknown",
            name: otherUserName || "Unknown",
            message: latestMessage?.content || "",
            date: latestMessage?.timestamp
              ? new Date(latestMessage.timestamp).toLocaleString([], {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : new Date(thread.created_at).toLocaleString([], {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                }),
            avatar: "path_to_avatar", // Placeholder avatar path
          };
        });

        // Dispatch conversations to redux store
        dispatch(setConversations(convs));

        // Prepare messages grouped by conversation id
        const messagesByConv = {};
        filteredThreads.forEach((thread) => {
          messagesByConv[thread.id] = thread.messages.map((message) => ({
            id: message.id,
            text: message.content || "",
            date: message.timestamp
              ? new Date(message.timestamp).toLocaleString([], {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : new Date(thread.updated_at).toLocaleString([], {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                }),
            sender: message.sender,
            isCurrentUser: message.sender === user.name,
          }));
        });

        // Dispatch messages grouped by conversation to redux store
        dispatch(setMessagesByConversation(messagesByConv));
      } catch (error) {
        // Handle any error during fetch
        console.error("Error fetching conversations:", error);
      }
    };

    getThreads();
  }, [user, dispatch]);

  // Show a loading state until Axios interceptors are ready
  if (!isInterceptorReady) {
    return <div>Loading...</div>;
  }

  // Render main app once ready
  return (
    <Router>
      <RoutesWrapper />
      {/* <ChatBar /> */}
      <ToastContainer />
    </Router>
  );
};
